import { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react';

// Types
import { Project } from '@shared/types';

// Hooks
import { useDebounce } from '@shared/hooks';

// Stores
import { useProjects, useIsProjectsLoading, useFetchProjects } from '@/stores';

// Helpers
import {
  findExactProjectMatch,
  getProjectMentionText,
  normalizeProjectQuery,
} from '@/components/ProjectMentionDropdown/helpers';

// Constants
import { KEYBOARD_EVENT } from '@shared/constants';

// Providers
import { useFirebase } from '@/providers/firebase';

interface UseMentionProjectsProps {
  debounceTime?: number;
}

const DEFAULT_MENTION_STATE = {
  isActive: false,
  query: '',
  startPosition: -1,
  endPosition: -1,
};

export const useMentionProjects = ({
  debounceTime = 300,
}: UseMentionProjectsProps) => {
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [selectedProjectIndex, setSelectedProjectIndex] = useState(-1);
  const [mentionState, setMentionState] = useState(DEFAULT_MENTION_STATE);

  const selectedProjectRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const projects = useProjects();
  const isLoading = useIsProjectsLoading();
  const fetchProjects = useFetchProjects();
  const { getToken } = useFirebase();

  // Debounce mention query to avoid excessive filtering
  const debouncedMentionQuery = useDebounce(mentionState.query, debounceTime);

  // Reset mention state to empty
  const resetMentionState = useCallback(() => {
    setMentionState(DEFAULT_MENTION_STATE);
  }, []);

  // Clear filtered projects list when no valid query
  const clearFilteredProjects = useCallback(() => {
    setFilteredProjects([]);
  }, []);

  // Scroll selected project into view
  const scrollSelectedProjectIntoView = useCallback(() => {
    setTimeout(() => {
      selectedProjectRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 0);
  }, []);

  // Detect # mentions and update state
  const detectMention = useCallback(
    (value: string, cursorPosition: number) => {
      // Find the last # before cursor
      const beforeCursor = value.substring(0, cursorPosition);
      const lastHashIndex = beforeCursor.lastIndexOf('#');

      // No # found, deactivate
      if (lastHashIndex === -1) {
        resetMentionState();
        return;
      }

      // Check for space after # (ends mention)
      const afterHash = value.substring(lastHashIndex + 1, cursorPosition);
      const hasSpace = afterHash.includes(' ');

      // Space found, deactivate
      if (hasSpace) {
        resetMentionState();
        return;
      }

      // Extract query and activate mention
      const query = afterHash;

      setMentionState({
        isActive: true,
        query,
        startPosition: lastHashIndex,
        endPosition: cursorPosition,
      });
    },
    [resetMentionState],
  );

  // Filter projects from store based on query
  const filterProjectsFromStore = useCallback(
    (query: string) => {
      const normalizedQuery = normalizeProjectQuery(query);

      // If query is empty (just #), show all projects
      if (!normalizedQuery) {
        setFilteredProjects(projects);
        return;
      }

      // Filter projects from store data
      const filtered = projects.filter(({ projectInfo }) => {
        const code = projectInfo.code?.toLowerCase() || '';
        const name = projectInfo.name?.toLowerCase() || '';

        return code.includes(normalizedQuery) || name.includes(normalizedQuery);
      });

      setFilteredProjects(filtered);
    },
    [projects],
  );

  // Filter projects when mention active and query changes
  useEffect(() => {
    // If mention is not active, clear filtered projects
    if (!mentionState.isActive) {
      clearFilteredProjects();
      return;
    }

    // If query is empty, use debounced query, otherwise use current query
    const queryToFilter =
      mentionState.query === '' ? debouncedMentionQuery : mentionState.query;

    filterProjectsFromStore(queryToFilter);
  }, [
    mentionState.isActive,
    debouncedMentionQuery,
    filterProjectsFromStore,
    clearFilteredProjects,
    mentionState.query,
  ]);

  // Fetch projects when mention becomes active
  useEffect(() => {
    const fetch = async () => {
      const token = await getToken();

      await fetchProjects({
        requestConfig: {
          token,
        },
      });
    };

    if (mentionState.isActive) {
      fetch();
    }

    // Don't need to put "getToken" in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionState.isActive, fetchProjects]);

  // Find exact project code match for auto-selection on space
  const checkForExactMatch = useCallback(() => {
    if (
      !mentionState.isActive ||
      !mentionState.query ||
      filteredProjects.length === 0
    ) {
      return null;
    }

    return findExactProjectMatch(filteredProjects, mentionState.query);
  }, [mentionState.isActive, mentionState.query, filteredProjects]);

  // Reset selected index when filtered projects change
  useEffect(() => {
    setSelectedProjectIndex(-1);
  }, [filteredProjects]);

  // Create selection result with formatted names
  const selectProject = useCallback(
    (project: Project) => ({
      project,
      displayName: getProjectMentionText(project),
      mentionText: `#${project.projectInfo.code}`,
      replaceStart: mentionState.startPosition,
      replaceEnd: mentionState.endPosition,
    }),
    [mentionState],
  );

  // Handle keyboard events: arrows, enter, escape, space
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!mentionState.isActive || filteredProjects.length === 0) return false;

      switch (event.key) {
        case KEYBOARD_EVENT.ARROW_DOWN:
          event.preventDefault();
          // Navigate down with circular wrap: last → first
          setSelectedProjectIndex((prev) => {
            const newIndex = prev < filteredProjects.length - 1 ? prev + 1 : 0;

            // Ensures DOM updates before scrolling to new selected item
            setTimeout(() => scrollSelectedProjectIntoView(), 0);
            return newIndex;
          });
          return true;

        case KEYBOARD_EVENT.ARROW_UP:
          event.preventDefault();
          // Navigate up with circular wrap: first → last
          setSelectedProjectIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : filteredProjects.length - 1;

            // Ensures DOM updates before scrolling to new selected item
            setTimeout(() => scrollSelectedProjectIntoView(), 0);
            return newIndex;
          });
          return true;

        case KEYBOARD_EVENT.ENTER:
          // Select highlighted project
          if (
            selectedProjectIndex >= 0 &&
            filteredProjects[selectedProjectIndex]
          ) {
            event.preventDefault();
            return selectProject(filteredProjects[selectedProjectIndex]);
          }
          return false;

        case KEYBOARD_EVENT.ESCAPE:
          event.preventDefault();

          // Close dropdown
          resetMentionState();
          return true;

        case KEYBOARD_EVENT.SPACE:
          // Auto-select if exact project code match exists
          if (mentionState.query && filteredProjects.length > 0) {
            const exactMatch = findExactProjectMatch(
              filteredProjects,
              mentionState.query,
            );

            if (exactMatch) {
              event.preventDefault();
              return selectProject(exactMatch);
            }
          }

          // No match, close dropdown
          resetMentionState();
          return false;

        default:
          return false;
      }
    },
    [
      mentionState.isActive,
      mentionState.query,
      filteredProjects,
      selectedProjectIndex,
      selectProject,
      scrollSelectedProjectIntoView,
      resetMentionState,
    ],
  );

  // Close dropdown and reset all state
  const closeMention = useCallback(() => {
    resetMentionState();
    clearFilteredProjects();
    setSelectedProjectIndex(-1);
  }, [resetMentionState, clearFilteredProjects]);

  return {
    // State
    projects: filteredProjects,
    isLoading,
    selectedProjectIndex,
    isMentionActive: mentionState.isActive,
    mentionQuery: mentionState.query,

    // Refs
    selectedProjectRef,
    dropdownRef,

    // Methods
    detectMention,
    selectProject,
    handleKeyDown,
    closeMention,
    checkForExactMatch,
    updateSelectedProjectIndex: setSelectedProjectIndex,
  };
};
